import { useCallback, useState } from 'react';

import {
  useLmmOpenBenodigdOpbouw,
  useLmmOpenTijdstip,
  useLmmSortDirection,
  useLmmSortField,
  useLmmTijdstippen,
  useSetLmmSort,
  useToggleLmmOpenBenodigdOpbouw,
} from '@/apps/lmm/state/selectors/useLmmVoorraadStore';
import SortUp from '@/assets/icons/SortUp';

import { getLmmTijdstipColumns, LMM_COLUMN_LABELS, LmmColumnKey } from '../../utils/lmmTableColumns';

import LmmExpandChevrons from './LmmExpandChevrons';

import styles from './LmmTableMaterieelHeaders.module.css';

export default function LmmTableMaterieelHeaders() {
  const lmmTijdstippen = useLmmTijdstippen();
  const lmmOpenTijdstip = useLmmOpenTijdstip();
  const lmmOpenBenodigdOpbouw = useLmmOpenBenodigdOpbouw();
  const lmmSortField = useLmmSortField();
  const lmmSortDirection = useLmmSortDirection();
  const setLmmSort = useSetLmmSort();
  const toggleLmmOpenBenodigdOpbouw = useToggleLmmOpenBenodigdOpbouw();

  const [showFieldSortButton, setShowFieldSortButton] = useState<{ materieel: boolean; overOfTekort: boolean }>({
    materieel: lmmSortField === 'materieel',
    overOfTekort: lmmSortField === 'overOfTekort',
  });

  /** Handle a sort click event */
  const onSortClick = (field: 'materieel' | 'overOfTekort') => () => {
    setLmmSort(field);
    setShowFieldSortButton((prev) => {
      const otherField = field === 'materieel' ? 'overOfTekort' : 'materieel';
      return { ...prev, [field]: true, [otherField]: false };
    });
  };

  /** Handle a sort keyboard event */
  const onSortKeydown = (field: 'materieel' | 'overOfTekort') => (event: React.KeyboardEvent) => {
    if (['Enter', 'NumpadEnter'].includes(event.key)) {
      setLmmSort(field);
      setShowFieldSortButton((prev) => {
        const otherField = field === 'materieel' ? 'overOfTekort' : 'materieel';
        return { ...prev, [field]: true, [otherField]: false };
      });
    }
  };

  const onMouseEnterSortButton = (field: 'materieel' | 'overOfTekort') => () =>
    setShowFieldSortButton((prev) => ({ ...prev, [field]: true }));

  const onMouseLeaveSortButton = useCallback(
    (field: 'materieel' | 'overOfTekort') => () =>
      setShowFieldSortButton((prev) => ({ ...prev, [field]: lmmSortField === field })),
    [lmmSortField],
  );

  const sortButton = (field: 'materieel' | 'overOfTekort', testId: string) => (
    <button
      data-testid={testId}
      onClick={onSortClick(field)}
      onKeyDown={onSortKeydown(field)}
      className={styles.iconSortUpButton}
    >
      <SortUp
        className={[
          lmmSortField === field ? '' : styles.iconMuted,
          lmmSortDirection === 'asc' ? styles.iconSortUp : styles.iconSortDown,
        ].join(' ')}
      />
    </button>
  );

  return (
    <div className={styles.tableHeaders}>
      <div
        className={[styles.cell, styles.tableHeaderSort, styles.matsoortCell].join(' ')}
        onMouseEnter={onMouseEnterSortButton('materieel')}
        onMouseLeave={onMouseLeaveSortButton('materieel')}
      >
        <div>Matsoort</div>
        {showFieldSortButton.materieel && <div>{sortButton('materieel', 'lmmSortMaterieel')}</div>}
      </div>
      <div className={[styles.cell, styles.brutoParkCell].join(' ')}>Bruto park</div>
      {lmmTijdstippen.map((tijdstip) => {
        const isOpen = tijdstip.label === lmmOpenTijdstip;
        const columns = getLmmTijdstipColumns(tijdstip.label, lmmOpenTijdstip, lmmOpenBenodigdOpbouw);

        const renderColumnHeader = (columnKey: LmmColumnKey) => {
          switch (columnKey) {
            case 'benodigd':
              return (
                <button
                  type='button'
                  data-testid={`lmmBenodigdToggle-${tijdstip.label}`}
                  className={styles.benodigdToggle}
                  onClick={toggleLmmOpenBenodigdOpbouw}
                >
                  <span>Benodigd</span>
                  <LmmExpandChevrons open={lmmOpenBenodigdOpbouw} />
                </button>
              );
            case 'ebksb':
              return isOpen && lmmOpenBenodigdOpbouw ? (
                <button
                  type='button'
                  data-testid={`lmmBenodigdToggle-${tijdstip.label}`}
                  className={styles.benodigdToggle}
                  onClick={toggleLmmOpenBenodigdOpbouw}
                >
                  <span>EBKSB</span>
                  <LmmExpandChevrons open={lmmOpenBenodigdOpbouw} />
                </button>
              ) : (
                LMM_COLUMN_LABELS[columnKey]
              );
            case 'overOfTekort':
              return isOpen ? (
                <div
                  className={styles.tableHeaderSort}
                  onMouseEnter={onMouseEnterSortButton('overOfTekort')}
                  onMouseLeave={onMouseLeaveSortButton('overOfTekort')}
                >
                  <div>{LMM_COLUMN_LABELS[columnKey]}</div>
                  <div>{showFieldSortButton.overOfTekort && sortButton('overOfTekort', 'lmmSortOverTekort')}</div>
                </div>
              ) : (
                LMM_COLUMN_LABELS[columnKey]
              );
            default:
              return LMM_COLUMN_LABELS[columnKey];
          }
        };

        return (
          <div key={tijdstip.label} className={styles.tijdstipGroup} style={{ flexGrow: columns.length }}>
            {columns.map((columnKey) => (
              <div key={columnKey} className={styles.cell}>
                {renderColumnHeader(columnKey)}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
